import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import {
    Box,
    Button,
    Dialog,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Package } from "lucide-react";
import { useForm } from "react-hook-form";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { useModalStyles } from "../../common/useModalStyles";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { OpenInNew } from "@mui/icons-material";
import { fetchProductsByUserRoleData, ProductInfo, useProductListReducer } from "../../../redux/slice/product/productListSlice";
import { sortNames } from "../../../utils/Utility";

interface ManageProductModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: productFormData) => void;
}
export type productFormData = {
    product: string | number;
}

const ManageProductModal = ({ open, onClose, onSubmit }: ManageProductModalProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        bodySx,
        footerSx,
        closeIconSx,
        dropdownWrapSx,
        datepickerWrapSx,
        primaryBtnSx,
        cancelBtnSx,
    } = useModalStyles();

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        getValues,
        formState: { errors },
    } = useForm<productFormData>({
        defaultValues: {
            product: ""
        },
        mode: "onSubmit",
    });

    const dispatch = useAppDispatch();
    const { productsByUserRole } = useProductListReducer();

    const handleClose = () => {
        setTimeout(() => {
            reset();
            onClose();
        }, 300);
    };
    const onFormSubmit = (data: productFormData) => {
        onSubmit(data);
        handleClose();
    };

    const FieldLabel = ({
        label,
        required = false,
    }: {
        label: string;
        required?: boolean;
    }) => (
        <Typography
            sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                color: tokens.textSecondary,
                mb: 0.7,
                display: "flex",
                alignItems: "center",
                gap: 0.3,
            }}
        >
            {label}
            {required && (
                <Box component="span" sx={{ color: tokens.accent }}>
                    *
                </Box>
            )}
        </Typography>
    );

    type Option = {
        value: string | number;
        label: string;
    };

    const handleProductChange = async () => {
        const product = getValues('product');
    };

    const productOptions = useMemo(() => {
        let options: Option[] = [];
        if (productsByUserRole && productsByUserRole.length > 0) {
            if (productsByUserRole.length === 1) {
                setValue('product', Number(productsByUserRole[0]?.productId));
            }
            const sortedProductList: ProductInfo[] = sortNames(productsByUserRole, 'productName');
            sortedProductList?.map((option) => {
                options.push({ label: option.productName, value: option.productId })
            });
        };
        return options;
    }, [productsByUserRole]);

    useEffect(() => {
        if (open) fetchProducts();
        else handleClose();
    }, [open]);

    const fetchProducts = async () => {
        const response = await dispatch(fetchProductsByUserRoleData(true));
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{ sx: paperSx(isMobile) }}
        >
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <Box sx={headerSx}>
                    <Box sx={iconBoxSx(tokens.accent)}>
                        <Package size={20} color={tokens.accent} strokeWidth={1.8} />
                    </Box>
                    <Box>
                        <Typography
                            fontWeight={700}
                            sx={{
                                fontSize: "1.1rem",
                                color: tokens.textPrimary,
                                lineHeight: 1.3,
                            }}
                        >
                            Manage Product
                        </Typography>
                        <Typography
                            sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}
                        >
                            Select a product to view or edit
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
                        <CloseIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                </Box>

                <Box sx={bodySx}>
                    <Box>
                        <FieldLabel label="Product Name" required />
                        <Box sx={dropdownWrapSx()}>
                            <CustomDropdownField
                                name="product"
                                control={control}
                                options={productOptions}
                                size="small"
                                placeholderText={
                                    productOptions.length === 0
                                        ? "No product found"
                                        : "Choose a product..."
                                }
                                sortOptions={false}
                                disabled={productOptions?.length === 1}
                                onChangeSelect={() => handleProductChange()}
                                rules={{ required: "Please select a product to continue." }}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box sx={footerSx}>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            type='submit'
                            startIcon={
                                <OpenInNew sx={{ fontSize: "1rem !important" }} />
                            }
                            sx={primaryBtnSx}
                        >
                            Manage Product
                        </Button>

                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
                            onClick={handleClose}
                            sx={cancelBtnSx}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </form>
        </Dialog>
    );
};

export default ManageProductModal;